import React, { useEffect, useState } from 'react';
import { fetchData, postData } from '../components/misc';
import { trackEvent } from '../utils/bridge.js';

const defaultBody = '<p>As-salamu alaykum,</p>\n<p></p>\n<p>JazakAllahu khair.</p>';

export default function NotificationsAdmin() {
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState(defaultBody);
    const [recipientSummary, setRecipientSummary] = useState(null);
    const [selectedBatch, setSelectedBatch] = useState(0);
    const [sendMode, setSendMode] = useState('batch');
    const [selectedRecipients, setSelectedRecipients] = useState([]);
    const [recipientSearch, setRecipientSearch] = useState('');
    const [loadingRecipients, setLoadingRecipients] = useState(true);
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchData('/api/sirri_api/notifications/recipients')
            .then((data) => {
                if (data && data.responsecode === 200) {
                    setRecipientSummary(data);
                    if (!data.batches || data.batches.length === 0) {
                        setSelectedBatch(0);
                    }
                }
            })
            .catch((err) => {
                console.error('Failed to load notification recipients', err);
                setMessage('Failed to load recipients.');
            })
            .finally(() => setLoadingRecipients(false));
    }, []);

    const batches = recipientSummary?.batches || [];
    const allRecipients = recipientSummary?.recipients || [];
    const selectedBatchInfo = batches.find((batch) => batch.index === Number(selectedBatch));
    const filteredRecipients = allRecipients.filter((email) =>
        email.toLowerCase().includes(recipientSearch.trim().toLowerCase())
    );

    const toggleRecipient = (email) => {
        setSelectedRecipients((previous) => {
            if (previous.includes(email)) {
                return previous.filter((item) => item !== email);
            }
            return [...previous, email];
        });
    };

    const selectVisibleRecipients = () => {
        setSelectedRecipients((previous) => Array.from(new Set([...previous, ...filteredRecipients])));
    };

    const clearSelectedRecipients = () => {
        setSelectedRecipients([]);
    };

    const sendNotification = () => {
        const cleanSubject = subject.trim();
        const cleanBody = body.trim();
        if (!cleanSubject || !cleanBody) {
            setMessage('Subject and body are required.');
            return;
        }

        const sendingSelectedUsers = sendMode === 'selected';
        if (sendingSelectedUsers && selectedRecipients.length === 0) {
            setMessage('Select at least one user first.');
            return;
        }
        if (!sendingSelectedUsers && !selectedBatchInfo) {
            setMessage('Select a recipient batch first.');
            return;
        }

        const confirmed = window.confirm(
            sendingSelectedUsers
                ? `Send this notification only to ${selectedRecipients.length} selected users?`
                : `Send this notification to batch ${Number(selectedBatch) + 1} (${selectedBatchInfo.count} users)?`
        );
        if (!confirmed) return;

        const payload = {
            subject: cleanSubject,
            body: cleanBody,
        };
        if (sendingSelectedUsers) {
            payload.recipients = selectedRecipients;
        } else {
            payload.batchIndex = Number(selectedBatch);
        }

        setSending(true);
        setMessage('');
        trackEvent('admin_notifications_send_pressed', {
            mode: sendingSelectedUsers ? 'selected' : 'batch',
            batchIndex: sendingSelectedUsers ? null : Number(selectedBatch),
            recipients: sendingSelectedUsers ? selectedRecipients.length : selectedBatchInfo.count,
        });
        postData('/api/sirri_api/notifications/send', payload)
            .then((data) => {
                if (data && data.responsecode === 200) {
                    const successMessage = data.mode === 'selected'
                        ? `Sent only to ${data.sent} selected users.`
                        : `Sent to ${data.sent} users in batch ${data.batch_index + 1} of ${data.batch_count}.`;
                    setMessage(successMessage);
                    trackEvent('admin_notifications_send_success', {
                        mode: data.mode,
                        batchIndex: data.batch_index,
                        sent: data.sent,
                    });
                } else {
                    setMessage(data?.error || 'Notification send failed.');
                }
            })
            .catch((err) => {
                console.error('Failed to send notification', err);
                setMessage('Notification send failed.');
            })
            .finally(() => setSending(false));
    };

    return (
        <div className="space-y-4 text-white">
            <div>
                <h1 className="text-2xl font-bold">Notifications</h1>
                <p className="text-sm text-gray-200">
                    Compose an HTML-capable email and send it to a batch or only selected registered users.
                </p>
            </div>

            <div className="bg-gray-900 p-4 rounded space-y-3">
                <label className="block">
                    <span className="block mb-1">Subject</span>
                    <input
                        className="w-full text-white p-2 rounded"
                        value={subject}
                        onChange={(event) => setSubject(event.target.value)}
                        placeholder="Email subject"
                    />
                </label>

                <label className="block">
                    <span className="block mb-1">Email body (HTML allowed)</span>
                    <textarea
                        className="w-full text-white p-2 rounded font-mono"
                        rows="12"
                        value={body}
                        onChange={(event) => setBody(event.target.value)}
                    />
                </label>

                <div className="space-y-2">
                    <span className="block mb-1">Send to</span>
                    <label className="inline-flex items-center mr-4">
                        <input
                            type="radio"
                            name="notification-send-mode"
                            value="batch"
                            checked={sendMode === 'batch'}
                            onChange={() => setSendMode('batch')}
                        />
                        <span className="ml-2">A recipient batch</span>
                    </label>
                    <label className="inline-flex items-center">
                        <input
                            type="radio"
                            name="notification-send-mode"
                            value="selected"
                            checked={sendMode === 'selected'}
                            onChange={() => setSendMode('selected')}
                        />
                        <span className="ml-2">Only selected users</span>
                    </label>
                </div>

                {sendMode === 'batch' ? (
                    <label className="block">
                        <span className="block mb-1">Recipient batch</span>
                        <select
                            className="text-white p-2 rounded"
                            value={selectedBatch}
                            onChange={(event) => setSelectedBatch(Number(event.target.value))}
                            disabled={loadingRecipients || batches.length === 0}
                        >
                            {batches.map((batch) => (
                                <option key={batch.index} value={batch.index}>
                                    Batch {batch.index + 1}: {batch.count} users ({batch.first} - {batch.last})
                                </option>
                            ))}
                        </select>
                        {loadingRecipients ? (
                            <p className="text-sm text-gray-300 mt-1">Loading recipients...</p>
                        ) : (
                            <p className="text-sm text-gray-300 mt-1">
                                {recipientSummary?.count || 0} total recipients across {recipientSummary?.batch_count || 0} batches.
                            </p>
                        )}
                    </label>
                ) : (
                    <div className="space-y-2">
                        <label className="block">
                            <span className="block mb-1">Select users</span>
                            <input
                                className="w-full text-white p-2 rounded"
                                value={recipientSearch}
                                onChange={(event) => setRecipientSearch(event.target.value)}
                                placeholder="Search by email"
                            />
                        </label>
                        <div className="flex gap-2 flex-wrap">
                            <button className="bg-gray-700 text-white px-3 py-1 rounded" type="button" onClick={selectVisibleRecipients}>
                                Select visible
                            </button>
                            <button className="bg-gray-700 text-white px-3 py-1 rounded" type="button" onClick={clearSelectedRecipients}>
                                Clear selected
                            </button>
                            <span className="text-sm text-gray-300 self-center">
                                {selectedRecipients.length} selected of {allRecipients.length} total users
                            </span>
                        </div>
                        <div className="max-h-64 overflow-y-auto border border-gray-700 rounded p-2 space-y-1">
                            {loadingRecipients ? (
                                <p className="text-sm text-gray-300">Loading recipients...</p>
                            ) : filteredRecipients.length === 0 ? (
                                <p className="text-sm text-gray-300">No matching recipients.</p>
                            ) : (
                                filteredRecipients.map((email) => (
                                    <label key={email} className="flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={selectedRecipients.includes(email)}
                                            onChange={() => toggleRecipient(email)}
                                        />
                                        <span>{email}</span>
                                    </label>
                                ))
                            )}
                        </div>
                    </div>
                )}

                <button
                    className="bg-primary text-white px-4 py-2 rounded disabled:opacity-50"
                    onClick={sendNotification}
                    disabled={sending || loadingRecipients || (sendMode === 'batch' && batches.length === 0)}
                >
                    {sending ? 'Sending...' : sendMode === 'selected' ? 'Send only selected users' : 'Send selected batch'}
                </button>
                {message ? <p className="text-sm text-yellow-200">{message}</p> : null}
            </div>

            <div className="bg-white text-black p-4 rounded">
                <h2 className="font-bold mb-2">Preview</h2>
                <h3 className="text-lg font-semibold mb-3">{subject || 'Subject preview'}</h3>
                <div className="border p-3 rounded" dangerouslySetInnerHTML={{ __html: body || '<p>Email body preview</p>' }} />
            </div>
        </div>
    );
}
